import type { ApiClient } from '../api/client';
import type { AttachmentKind } from '../api/types';

export interface AttachmentRef {
  blobKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  kind: AttachmentKind;
  /**
   * Markers dropped on this capture, normalized to 0..1 (I19222, ADR 0030).
   *
   * Set by `submitFeedback` for the leading capture and by nothing else, which
   * is why `uploadFile` does not take it: an upload is bytes and a name, and a
   * marker is neither.
   */
  pins?: { x: number; y: number; text: string }[];
}

/** The only two `ApiClient` methods an upload needs — narrowed so tests can pass a minimal fake. */
type UploadApi = Pick<ApiClient, 'createUpload' | 'putBlob'>;

/**
 * Uploads a single file/blob and returns the reference the ingest request payload embeds.
 * Two steps: ask the ingest API for a SAS upload target (`createUpload`), then PUT the bytes
 * straight to blob storage (`putBlob`) — never through this widget's own backend.
 *
 * This is called at SUBMIT time only (Task 9's job), not on file selection — `<Attachments>`
 * just holds `File` objects until then. It does NOT run `validateFile`; the caller is expected
 * to have already validated (the Attachments UI does, on selection) so this stays a thin,
 * single-purpose upload primitive. Throws whatever `createUpload`/`putBlob` throw — network
 * failures and the client's typed API errors alike — leaving retry/skip/surface-to-user
 * decisions to the caller.
 */
export async function uploadFile(
  api: UploadApi,
  file: File | Blob,
  kind: AttachmentKind,
  fileName?: string,
): Promise<AttachmentRef> {
  const resolvedFileName = fileName ?? (file instanceof File ? file.name : 'attachment');
  // Plain `Blob`s (e.g. a canvas-captured screenshot) often carry no `.type` at all.
  const contentType = file.type || 'application/octet-stream';

  const { url, blobKey } = await api.createUpload({
    fileName: resolvedFileName,
    contentType,
    kind,
  });

  await api.putBlob(url, file, contentType);

  return {
    blobKey,
    fileName: resolvedFileName,
    contentType,
    sizeBytes: file.size,
    kind,
  };
}
